/** Coalesced refresh owner with capped, jittered self-heal after failures. */

const normalizeRequestSequence = value => {
  if (value == null || String(value).trim() === '') return null;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed >= 0 ? parsed : null;
};

const normalizeListCacheVersion = value => {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized === '' ? null : normalized;
};

const normalizeGenerationDomain = value => {
  if (value == null) return null;
  const normalized = String(value).trim().toLowerCase();
  return normalized === '' ? null : normalized;
};

export const sportsnewListGenerationDomain = ({ mode, sport } = {}) => {
  const normalizedMode = normalizeGenerationDomain(mode);
  const normalizedSport = normalizeGenerationDomain(sport);
  if (!normalizedMode) return null;
  // The list REST contract omits `sport` for the all-sports view while the
  // Socket subscription names that same generation domain explicitly `all`.
  // Canonicalize both representations here so accepted REST evidence can
  // settle its own membership fence instead of entering perpetual recovery.
  return `${normalizedMode}|${normalizedSport || 'all'}`;
};

const laterRequestSequence = (first, second) => {
  const normalizedFirst = normalizeRequestSequence(first);
  const normalizedSecond = normalizeRequestSequence(second);
  if (normalizedFirst == null) return normalizedSecond;
  if (normalizedSecond == null) return normalizedFirst;
  return Math.max(normalizedFirst, normalizedSecond);
};

export const sportsnewListCacheVersionSatisfiesMembershipBoundary = (
  listCacheVersion,
  requiredListCacheVersion,
) => {
  const required = normalizeListCacheVersion(requiredListCacheVersion);
  if (required == null) return true;
  const current = normalizeListCacheVersion(listCacheVersion);
  if (current == null) return false;
  if (current === required) return true;
  const currentNumber = /^\d+$/.test(current) ? Number(current) : null;
  const requiredNumber = /^\d+$/.test(required) ? Number(required) : null;
  return Number.isSafeInteger(currentNumber) && Number.isSafeInteger(requiredNumber)
    && currentNumber >= requiredNumber;
};

export const sportsnewRequestSequenceSatisfiesMembershipBoundary = (
  requestSequence,
  requiredRequestSequence,
) => {
  const required = normalizeRequestSequence(requiredRequestSequence);
  if (required == null) return true;
  const request = normalizeRequestSequence(requestSequence);
  return request != null && request >= required;
};

/**
 * Close only the initial REST-snapshot -> Socket.IO connection gap.
 *
 * A socket subscription that happens after the current list key resolved only
 * needs recovery when the server's subscribe-time list-cache version differs
 * from the REST snapshot. This covers both REST-before-connect and
 * connect-before-REST ordering. Missing version evidence fails safe with one
 * refresh. Reconnects use the same generation fence: equality is free, while
 * a gap or missing evidence owns one coalesced catch-up.
 */
export const scheduleSportsnewInitialSocketMembershipCatchup = ({
  activeRequestKey,
  resolvedSnapshot,
  currentListCacheVersion,
  requiredRequestSequence,
  missingVersionRequiredRequestSequence,
  generationDomain,
  coordinator,
} = {}) => {
  if (activeRequestKey == null || resolvedSnapshot?.requestKey == null) return false;
  if (String(activeRequestKey) !== String(resolvedSnapshot.requestKey)) return false;
  const restVersion = normalizeListCacheVersion(resolvedSnapshot.listCacheVersion);
  const subscribeVersion = normalizeListCacheVersion(currentListCacheVersion);
  if (subscribeVersion != null
      && sportsnewListCacheVersionSatisfiesMembershipBoundary(
        restVersion,
        subscribeVersion,
      )) {
    return false;
  }
  if (typeof coordinator?.schedule !== 'function') return false;
  const requestBoundary = subscribeVersion == null
    ? laterRequestSequence(
      requiredRequestSequence,
      missingVersionRequiredRequestSequence,
    )
    : normalizeRequestSequence(requiredRequestSequence);
  coordinator.schedule(requestBoundary, subscribeVersion, generationDomain);
  return true;
};

export function createSportsnewMembershipRefreshCoordinator({
  getRefresh,
  onPendingChange,
  coalesceMs = 50,
  retryDelayMs = 1000,
  maxRetries = 1,
  recoveryBaseDelayMs = 5000,
  recoveryMaxDelayMs = 60000,
  recoveryJitterRatio = 0.2,
  random = Math.random,
  setTimer = setTimeout,
  clearTimer = clearTimeout,
} = {}) {
  let timer = null;
  let timerKind = null;
  let inFlight = null;
  let inFlightGenerationDomainRevision = null;
  let pending = false;
  let retryCount = 0;
  let recoveryCount = 0;
  let requiredRequestSequence = null;
  let requiredListCacheVersion = null;
  let generationDomain = null;
  let generationDomainRevision = 0;
  let resolvedExternallyRevision = null;
  let stopped = false;
  let terminalBlocked = false;

  const notify = value => onPendingChange?.(value);

  const resultSatisfiesGeneration = (result) => {
    if (!result || typeof result !== 'object') return false;
    if (result?.membershipSettled === false) return false;
    if (generationDomain != null
        && normalizeGenerationDomain(result?.generationDomain) !== generationDomain) {
      return false;
    }
    return sportsnewRequestSequenceSatisfiesMembershipBoundary(
      result?.requestSequence,
      requiredRequestSequence,
    ) && sportsnewListCacheVersionSatisfiesMembershipBoundary(
      result?.listCacheVersion,
      requiredListCacheVersion,
    );
  };

  const replaceGenerationDomain = (nextGenerationDomain, force = false) => {
    const normalizedDomain = normalizeGenerationDomain(nextGenerationDomain);
    if (!force && normalizedDomain === generationDomain) return false;
    generationDomain = normalizedDomain;
    generationDomainRevision += 1;
    terminalBlocked = false;
    pending = false;
    retryCount = 0;
    recoveryCount = 0;
    requiredRequestSequence = null;
    requiredListCacheVersion = null;
    resolvedExternallyRevision = null;
    if (timer) clearTimer(timer);
    timer = null;
    timerKind = null;
    notify(false);
    return true;
  };

  const queue = (run, delayMs, kind) => {
    if (stopped || terminalBlocked || timer || inFlight) return;
    timerKind = kind;
    timer = setTimer(run, delayMs);
  };

  const run = () => {
    timer = null;
    timerKind = null;
    pending = false;
    let succeeded = false;
    let terminal = false;
    let minimumDelayMs = 0;
    const flightGenerationDomainRevision = generationDomainRevision;
    const flight = Promise.resolve().then(() => {
      if (stopped || terminalBlocked) return { recoveryOutcome: 'terminal' };
      const refresh = getRefresh?.();
      return typeof refresh === 'function' ? refresh() : undefined;
    });
    inFlight = flight;
    inFlightGenerationDomainRevision = flightGenerationDomainRevision;
    flight.then((result) => {
      succeeded = resultSatisfiesGeneration(result);
      terminal = result?.recoveryOutcome === 'terminal';
      minimumDelayMs = Math.max(0, Number(result?.retryAfterMs) || 0);
    }).catch(() => {}).finally(() => {
      if (inFlight === flight) {
        inFlight = null;
        inFlightGenerationDomainRevision = null;
      }
      if (stopped) return;
      if (flightGenerationDomainRevision !== generationDomainRevision) {
        if (pending) queue(run, coalesceMs, 'coalesce');
        return;
      }
      if (resolvedExternallyRevision === flightGenerationDomainRevision) succeeded = true;
      if (terminal && !succeeded) {
        replaceGenerationDomain(generationDomain, true);
        terminalBlocked = true;
        return;
      }
      if (pending && !succeeded) {
        queue(run, coalesceMs, 'coalesce');
        return;
      }
      if (pending) pending = false;
      if (!succeeded && retryCount < maxRetries) {
        retryCount += 1;
        queue(run, Math.max(retryDelayMs, minimumDelayMs), 'retry');
        return;
      }
      if (!succeeded) {
        retryCount = 0;
        recoveryCount += 1;
        const baseDelay = Math.min(
          recoveryMaxDelayMs,
          recoveryBaseDelayMs * (2 ** Math.max(0, recoveryCount - 1)),
        );
        const jitterRatio = Math.max(0, Math.min(1, Number(recoveryJitterRatio) || 0));
        const jitter = 1 - jitterRatio + ((Number(random?.()) || 0) * jitterRatio * 2);
        notify(true);
        queue(run, Math.max(1, minimumDelayMs, Math.round(baseDelay * jitter)), 'recovery');
        return;
      }
      retryCount = 0;
      recoveryCount = 0;
      requiredRequestSequence = null;
      requiredListCacheVersion = null;
      resolvedExternallyRevision = null;
      notify(false);
    });
  };

  return Object.freeze({
    // Initial REST failure may precede any Socket subscription. Reuse this
    // owner only after the REST flight is removed; never reset existing backoff.
    ensureRecovery(nextRequiredRequestSequence, nextGenerationDomain, minimumDelayMs = 0) {
      if (stopped || terminalBlocked || timer || inFlight) return false;
      const normalizedDomain = normalizeGenerationDomain(nextGenerationDomain);
      if (normalizedDomain !== generationDomain) replaceGenerationDomain(normalizedDomain);
      requiredRequestSequence = laterRequestSequence(requiredRequestSequence, nextRequiredRequestSequence);
      pending = true;
      notify(true);
      queue(run, Math.max(recoveryBaseDelayMs, Number(minimumDelayMs) || 0), 'recovery');
      return true;
    },
    markTerminal() {
      if (stopped) return false;
      replaceGenerationDomain(generationDomain, true);
      terminalBlocked = true;
      return true;
    },
    resumeExplicitly() {
      if (stopped) return false;
      terminalBlocked = false;
      return true;
    },
    schedule(
      nextRequiredRequestSequence = null,
      nextRequiredListCacheVersion = null,
      nextGenerationDomain = null,
    ) {
      if (stopped || terminalBlocked) return false;
      const normalizedDomain = normalizeGenerationDomain(nextGenerationDomain);
      if (normalizedDomain != null && normalizedDomain !== generationDomain) {
        replaceGenerationDomain(normalizedDomain);
      }
      const normalizedRequired = normalizeRequestSequence(nextRequiredRequestSequence);
      if (normalizedRequired != null) {
        requiredRequestSequence = requiredRequestSequence == null
          ? normalizedRequired
          : Math.max(requiredRequestSequence, normalizedRequired);
      }
      const normalizedListVersion = normalizeListCacheVersion(nextRequiredListCacheVersion);
      if (normalizedListVersion != null) {
        const currentRequired = requiredListCacheVersion;
        requiredListCacheVersion = currentRequired == null
          || sportsnewListCacheVersionSatisfiesMembershipBoundary(
            normalizedListVersion,
            currentRequired,
          )
          ? normalizedListVersion
          : currentRequired;
      }
      resolvedExternallyRevision = null;
      if (timer && timerKind !== 'coalesce') {
        clearTimer(timer);
        timer = null;
        timerKind = null;
      }
      pending = true;
      retryCount = 0;
      recoveryCount = 0;
      notify(true);
      queue(run, coalesceMs, 'coalesce');
    },
    markResolved(result) {
      if (stopped || !resultSatisfiesGeneration(result)) return false;
      terminalBlocked = false;
      resolvedExternallyRevision = generationDomainRevision;
      pending = false;
      retryCount = 0;
      recoveryCount = 0;
      if (timer) clearTimer(timer);
      timer = null;
      timerKind = null;
      notify(false);
      if (!inFlight || inFlightGenerationDomainRevision !== generationDomainRevision) {
        requiredRequestSequence = null;
        requiredListCacheVersion = null;
        resolvedExternallyRevision = null;
      }
      return true;
    },
    replaceDomain(nextGenerationDomain, { reset = false } = {}) {
      if (stopped) return false;
      return replaceGenerationDomain(nextGenerationDomain, reset);
    },
    cancel() {
      stopped = true;
      pending = false;
      if (timer) clearTimer(timer);
      timer = null;
      timerKind = null;
      resolvedExternallyRevision = null;
    },
    state() {
      return Object.freeze({
        scheduled: timer !== null,
        timerKind,
        inFlight: inFlight !== null,
        inFlightGenerationDomainRevision,
        pending,
        retryCount,
        recoveryCount,
        requiredRequestSequence,
        requiredListCacheVersion,
        generationDomain,
        generationDomainRevision,
        resolvedExternallyRevision,
        stopped,
        terminalBlocked,
      });
    },
  });
}

export default Object.freeze({
  createSportsnewMembershipRefreshCoordinator,
  scheduleSportsnewInitialSocketMembershipCatchup,
  sportsnewListGenerationDomain,
  sportsnewListCacheVersionSatisfiesMembershipBoundary,
  sportsnewRequestSequenceSatisfiesMembershipBoundary,
});
